import { HandleType } from '@/enums/handle-type.enum';
import { NodeContentSizing } from '@/enums/node-content-sizing.enum';
import { NodeType } from '@/enums/node-type.enum';
import { clampValue } from '@/utils/numbers';

export const DEFAULT_NODE_WIDTH = 460;

export const SMALL_NODE_WIDTH = 250;

export const MAX_NODE_WIDTH = DEFAULT_NODE_WIDTH * 3;

/** Left + right padding on the node shell (`p: 2` at MUI's 8px spacing unit). */
export const NODE_HORIZONTAL_CHROME = 32;

const FLAG_OFF_APPLIED_BOX_SIZE_TYPES = new Set<NodeType>([
  NodeType.String,
  NodeType.PromptV3,
  NodeType.PromptConcat,
  NodeType.CustomModelV2,
  NodeType.Array,
  NodeType.MuxV2,
  NodeType.Import,
  NodeType.MediaIterator,
]);

export const shouldApplyStoredBoxSize = (nodeType: string | undefined, isDiagonalResizeEnabled: boolean): boolean =>
  isDiagonalResizeEnabled || (nodeType !== undefined && FLAG_OFF_APPLIED_BOX_SIZE_TYPES.has(nodeType as NodeType));

/** Types whose `data.width`/`data.height` mean the 3D viewport, not the node box. */
export const MEDIA_VIEWPORT_DIMENSION_NODE_TYPES = new Set<string>([
  NodeType.Import,
  NodeType.MediaIterator,
  'point_cloud_from_depth',
]);

const FIXED_HEIGHT_MEDIA_FRAME_TYPES = new Set<string>(['audio', 'text']);

const hasResizableMediaAspectRatio = (assetType: string | undefined): boolean =>
  assetType !== undefined && !FIXED_HEIGHT_MEDIA_FRAME_TYPES.has(assetType);

/**
 * Pass the type the node *will* show when it hasn't produced anything yet, so an empty node and a
 * full one resize by the same rule.
 */
export const getNodeContentSizing = (
  isSingleView: boolean,
  assetType: string | undefined,
  hasGridContent = false,
): NodeContentSizing => {
  if (isSingleView && hasResizableMediaAspectRatio(assetType)) return NodeContentSizing.Media;
  if (!isSingleView && hasGridContent) return NodeContentSizing.Grid;
  return NodeContentSizing.Box;
};

export interface NodeResizeLimits {
  defaultHeight?: number;
  minHeight: number;
  maxHeight: number;
  autoResizeMaxHeight: number;
  /** Fixed vertical chrome, excluded from the aspect-ratio calculation. */
  chromeHeight: number;
  minWidth: number;
  maxWidth: number;
}

export interface ResizableDimensions extends NodeResizeLimits {
  defaultHeight: number;
  defaultTextareaHeight: number;
}

export const TEXTAREA_BASE_HEIGHT = 430;

const RATIO_LOCKED_OUTPUT_TYPES = new Set<string>([
  HandleType.Image,
  HandleType.Video,
  HandleType.ThreeDee,
  HandleType.Mask,
]);

const isRatioLockedOutputType = (assetType: string | undefined): boolean =>
  assetType !== undefined && RATIO_LOCKED_OUTPUT_TYPES.has(assetType);

export const contentSizingForOutputType = (nodeOutputType: string | undefined): NodeContentSizing =>
  isRatioLockedOutputType(nodeOutputType) ? NodeContentSizing.Media : NodeContentSizing.Box;

const MEDIA_FRAME_DEFAULT_HEIGHT = 430;

export const getMediaFramePlaceholderSx = (
  nodeOutputType: string | undefined,
  isDiagonalResizeEnabled: boolean,
): { aspectRatio: string } | { flex: number; minHeight: number } | { height: string } => {
  if (!isDiagonalResizeEnabled) return { height: `${MEDIA_FRAME_DEFAULT_HEIGHT}px` };
  if (isRatioLockedOutputType(nodeOutputType)) return { aspectRatio: '1 / 1' };
  return { flex: 1, minHeight: MEDIA_FRAME_DEFAULT_HEIGHT };
};

/**
 * Height cap for a multi-file grid in a node the user never resized. The node hugs the grid, so this
 * is what keeps adding files from growing the node — the extra rows scroll instead. A resized node
 * fills its own height and ignores this.
 */
export const GRID_SCROLL_DEFAULT_MAX_HEIGHT = 436;

export const getResizableDimensions = (
  chromeHeight: number,
  size: 'large' | 'small' = 'large',
): ResizableDimensions => {
  const textareaHeight = Math.round(size === 'large' ? TEXTAREA_BASE_HEIGHT : (TEXTAREA_BASE_HEIGHT * 2) / 3);
  return {
    defaultHeight: textareaHeight + chromeHeight,
    defaultTextareaHeight: textareaHeight,
    minHeight: Math.round(TEXTAREA_BASE_HEIGHT / 3) + chromeHeight,
    maxHeight: TEXTAREA_BASE_HEIGHT * 3 + chromeHeight,
    autoResizeMaxHeight: Math.round(TEXTAREA_BASE_HEIGHT * 1.2) + chromeHeight,
    chromeHeight,
    minWidth: size === 'small' ? SMALL_NODE_WIDTH : DEFAULT_NODE_WIDTH,
    maxWidth: MAX_NODE_WIDTH,
  };
};

/** Initial size of a new prompt node before the user resizes it. */
export const PROMPT_NODE_RESIZABLE_DIMENSIONS = getResizableDimensions(120, 'small');

export const DEFAULT_RESIZABLE_DIMENSIONS: ResizableDimensions = getResizableDimensions(70, 'small');
export const LARGE_NODE_RESIZABLE_DIMENSIONS: ResizableDimensions = getResizableDimensions(70, 'large');

/**
 * Node box dimensions for a diagonal, aspect-ratio-preserving resize. The ratio applies to the
 * content area, not the outer box — chrome does not scale, so locking the outer box would leave a
 * letterbox gap that grows with the node.
 *
 * Width drives and height follows, so at the bottom end the ratio yields to `minWidth` — unless the
 * caller passes `minBoxHeight`, which bottoms both dimensions out together.
 */
export const getDiagonalResizeDimensions = ({
  width,
  contentRatio,
  chromeHeight,
  horizontalChrome = NODE_HORIZONTAL_CHROME,
  limits,
  clampWidthToMaxHeight = true,
  minBoxHeight,
  minDiagonalHeight,
  minDiagonalWidth,
}: {
  width: number;
  contentRatio: number;
  chromeHeight: number;
  horizontalChrome?: number;
  limits: NodeResizeLimits;
  clampWidthToMaxHeight?: boolean;
  /**
   * Height at which a shrink has to bottom out, above `limits.minHeight` — a grid passes the height it
   * gets when nobody resized it. The width bottoms out at the matching value, so the ratio survives the
   * floor instead of the box going narrower without getting shorter.
   */
  minBoxHeight?: number;
  minDiagonalHeight?: number;
  minDiagonalWidth?: number;
}): { width: number; height: number } => {
  const safeRatio = Number.isFinite(contentRatio) && contentRatio > 0 ? contentRatio : 1;

  const diagonalWidthFloor = clampValue(minDiagonalWidth ?? limits.minWidth, limits.minWidth, limits.maxWidth);

  const widthAtMaxHeight = (limits.maxHeight - chromeHeight) * safeRatio + horizontalChrome;
  const effectiveMaxWidth = clampWidthToMaxHeight
    ? Math.max(diagonalWidthFloor, Math.min(limits.maxWidth, widthAtMaxHeight))
    : limits.maxWidth;

  const diagonalFloor = clampValue(minDiagonalHeight ?? limits.minHeight, limits.minHeight, limits.maxHeight);

  // Only a caller-supplied floor turns into a width floor: on `limits.minHeight` alone the ratio yields
  // to `minWidth`, which is what keeps ultra-wide media reachable at the smallest node size.
  const minHeight = clampValue(minBoxHeight ?? diagonalFloor, diagonalFloor, limits.maxHeight);
  const widthAtMinHeight = (minHeight - chromeHeight) * safeRatio + horizontalChrome;
  const effectiveMinWidth =
    minBoxHeight === undefined
      ? diagonalWidthFloor
      : Math.min(Math.max(diagonalWidthFloor, widthAtMinHeight), effectiveMaxWidth);

  const clampedWidth = Math.round(clampValue(width, effectiveMinWidth, effectiveMaxWidth));
  const contentWidth = Math.max(0, clampedWidth - horizontalChrome);
  const derivedHeight = contentWidth / safeRatio + chromeHeight;

  return {
    width: clampedWidth,
    height: Math.round(clampValue(derivedHeight, minHeight, limits.maxHeight)),
  };
};

export const LIST_NODE_ITEM_GAP = 6;
const LIST_NODE_ITEM_HEIGHT = 32;
/** Space for the header, its margin, shell padding and selected-node borders. */
const LIST_NODE_BASE_CHROME = 80;
const LIST_NODE_ADD_BUTTON_HEIGHT = 28;

export const getListNodeResizeLimits = ({
  itemCount,
  hasDelimiterRow,
  hasAddButton,
}: {
  itemCount: number;
  hasDelimiterRow: boolean;
  hasAddButton: boolean;
}): NodeResizeLimits => {
  let chromeHeight = LIST_NODE_BASE_CHROME;
  if (hasDelimiterRow) chromeHeight += LIST_NODE_ITEM_HEIGHT + LIST_NODE_ITEM_GAP;
  if (hasAddButton) chromeHeight += LIST_NODE_ADD_BUTTON_HEIGHT + LIST_NODE_ITEM_GAP;

  const contentMaxHeight =
    itemCount * LIST_NODE_ITEM_HEIGHT + Math.max(0, itemCount - 1) * LIST_NODE_ITEM_GAP + chromeHeight;
  const visibleItemCount = Math.min(itemCount, 2);
  const contentMinHeight =
    visibleItemCount * LIST_NODE_ITEM_HEIGHT + Math.max(0, visibleItemCount - 1) * LIST_NODE_ITEM_GAP;
  return {
    minHeight: contentMinHeight + chromeHeight,
    maxHeight: contentMaxHeight,
    autoResizeMaxHeight: contentMaxHeight,
    chromeHeight,
    minWidth: DEFAULT_NODE_WIDTH,
    maxWidth: MAX_NODE_WIDTH,
  };
};
